/**
 * How an edge node's addresses are shown — the fleet table, its side panel and
 * the node page all use these, so the three can never disagree about what an
 * address means.
 *
 * Three rules, each from a way these fields were misread before:
 *
 * * **Hidden is not missing.** Addresses are operator-only and the server
 *   nulls them for a viewer (`addresses_hidden`). A viewer sees "Hidden —
 *   operator role", never "Not reported", which would claim the node is silent.
 * * **A container address is not a LAN address.** Agents before 1.1.0 ran on a
 *   Docker bridge and reported the container's 172.x. It is shown, because it
 *   is what the node said, but flagged, because nobody can reach the machine
 *   on it.
 * * **The mesh address says where it came from.** An owner's link to a NetBird
 *   peer is authoritative (a person confirmed it); the agent's own report is
 *   used when there is no link, or when the link has no recorded peer address
 *   yet. When both exist and disagree, the link's address is shown and the
 *   disagreement is called out — it usually means a node was re-installed and
 *   joined the mesh as a new peer. The hover hints therefore say only where
 *   the shown address came from, never what the other side has or lacks.
 */

import { Badge } from './Badge';
import type { EdgeMeshSource, EdgeNodeResponse } from '../types/api';

export const ADDRESSES_HIDDEN_TEXT = 'Hidden — operator role';

const SOURCE_LABEL: Record<EdgeMeshSource, string> = {
  agent: 'reported by the agent',
  'linked-peer': 'linked NetBird peer',
  'agent+link': 'linked peer, agent agrees',
};

const SOURCE_HINT: Record<EdgeMeshSource, string> = {
  agent: 'The node’s agent reported this address from its wt0 interface; no linked peer’s address is on record.',
  'linked-peer': 'An owner linked this node to a NetBird peer; this is that peer’s last-known address.',
  'agent+link': 'An owner linked this node to a NetBird peer, and the agent reports the same address.',
};

export function meshSourceLabel(source: EdgeMeshSource | null): string {
  return source ? SOURCE_LABEL[source] ?? source : '';
}

/** The mesh address's provenance as a small badge. */
export function MeshSourceBadge({ source }: { source: EdgeMeshSource | null }) {
  if (!source) return null;
  return (
    <Badge tone={source === 'agent' ? 'neutral' : 'info'} small title={SOURCE_HINT[source]}>
      {SOURCE_LABEL[source] ?? source}
    </Badge>
  );
}

export const CONFLICT_TEXT =
  'The agent reports a different mesh address from the linked NetBird peer. The link is shown; check whether the node re-joined the mesh as a new peer.';

export const CONTAINER_TEXT =
  'Container address: this agent predates 1.1.0 and reports its Docker bridge address, not the machine’s LAN address.';

/**
 * Every address as one searchable string, for the table's sort/search key.
 * Empty for a viewer, so a viewer's search can never match on an address the
 * server did not send anyway.
 */
export function addressSearchText(node: EdgeNodeResponse): string | null {
  if (node.addresses_hidden) return null;
  const parts = [node.private_ip, node.public_ip, node.mesh_ip].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : null;
}

/** LAN, public and mesh stacked in monospace — the fleet table's cell. */
export function AddressesCell({ node }: { node: EdgeNodeResponse }) {
  if (node.addresses_hidden) {
    return <span className="muted">{ADDRESSES_HIDDEN_TEXT}</span>;
  }
  const rows: { label: string; value: string | null; note?: string; warn?: boolean }[] = [
    {
      label: 'LAN',
      value: node.private_ip,
      note: node.private_ip && node.private_ip_is_container ? 'container' : undefined,
      warn: node.private_ip_is_container,
    },
    { label: 'Public', value: node.public_ip },
    {
      label: 'Mesh',
      value: node.mesh_ip,
      note: node.mesh_ip_conflict ? 'conflict' : undefined,
      warn: node.mesh_ip_conflict,
    },
  ];
  if (rows.every((r) => r.value === null)) {
    return <span className="muted">Not reported</span>;
  }
  return (
    <div className="edge-cell">
      {rows.map((r) =>
        r.value ? (
          <span
            key={r.label}
            className="edge-cell__note mono"
            title={
              r.label === 'LAN' && r.warn
                ? CONTAINER_TEXT
                : r.label === 'Mesh'
                  ? [meshSourceLabel(node.mesh_ip_source), r.warn ? CONFLICT_TEXT : '']
                      .filter(Boolean)
                      .join('. ')
                  : undefined
            }
          >
            {r.label} {r.value}
            {r.note ? <strong> ({r.note})</strong> : null}
          </span>
        ) : null,
      )}
    </div>
  );
}
